import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, HardHat, Plus, X, Check, Calendar, Archive, FileText, ChevronDown, ChevronRight, Search, Pencil, Upload, Download, Package, Paperclip } from 'lucide-react';
import { generateTeamMemberTasksPDF } from '../utils/teamMemberTasksPdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Unified 8-item list: task type = milestone key = upload folder key
export const TASK_TYPES = [
  { key: 'schematicDrawing',       label: 'Schematic Drawing' },
  { key: 'detailEngineeringDesign',label: 'Detail Engineering Design' },
  { key: 'soilReport',             label: 'Soil Report' },
  { key: 'calculationReport',      label: 'Calculation Report' },
  { key: 'boqRab',                 label: 'BOQ And RAB' },
  { key: 'rksOutlineSpec',         label: 'RKS And Outline Specifications' },
  { key: 'forConsDrawing',         label: 'For Cons Drawing' },
  { key: 'others',                 label: 'Others' },
];

// Legacy task-type keys → new keys (backward compat)
const LEGACY_KEY_MAP = {
  rks: 'rksOutlineSpec',
  structureModelling: 'others',
  forConstructionDrawings: 'forConsDrawing',
  billOfQuantityRAB: 'boqRab',
};
const normalizeTaskTypeKey = (k) => LEGACY_KEY_MAP[k] || k;
const FOLDER_LABEL = (k) => (TASK_TYPES.find(t => t.key === k)?.label) || 'Others';

// Project stages (Status Stepper)
export const STAGES = [
  { key: 'schematic',    label: 'Schematic' },
  { key: 'ded',          label: 'DED' },
  { key: 'tender',       label: 'Tender' },
  { key: 'construction', label: 'For Construction' },
];

const EngineeringPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('input');
  const [bookFilter, setBookFilter] = useState('freelance'); // 'freelance' | 'company'
  const [projects, setProjects] = useState([]);
  const [team, setTeam] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openProject, setOpenProject] = useState(null); // project object for tasks dialog
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [taskEdit, setTaskEdit] = useState({ description: '', deadline: '' });
  const [projectFiles, setProjectFiles] = useState([]);   // files for open project
  const [uploadFor, setUploadFor] = useState(null);       // { taskId, personName, folderKey }
  const [uploading, setUploading] = useState(false);
  const [backingUp, setBackingUp] = useState({});          // {projectId: bool}

  // Team Summary state
  const [summarySearch, setSummarySearch] = useState('');
  const [summaryOverdueOnly, setSummaryOverdueOnly] = useState(false);
  const [summaryBook, setSummaryBook] = useState('all'); // 'all' | 'freelance' | 'company'
  const [expandedMembers, setExpandedMembers] = useState({});

  // Input Task form
  const [form, setForm] = useState({
    projectId: '',
    taskType: '',     // one of TASK_TYPES.key or 'custom'
    customText: '',
    assignees: [],    // list of team member names
    deadline: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = async () => {
    try {
      const [p, t, tk] = await Promise.all([
        axios.get(`${API}/projects?archived=false`),
        axios.get(`${API}/team`),
        axios.get(`${API}/tasks`),
      ]);
      setProjects(p.data);
      setTeam(t.data);
      setTasks(tk.data);
      setLoading(false);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load engineering data');
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); }, []);

  // ============= Input Task =============
  const toggleAssignee = (name) => {
    setForm((prev) => ({
      ...prev,
      assignees: prev.assignees.includes(name)
        ? prev.assignees.filter(n => n !== name)
        : [...prev.assignees, name],
    }));
  };

  const resolveDescription = () => {
    if (form.taskType === 'custom') return form.customText.trim();
    const preset = TASK_TYPES.find(t => t.key === form.taskType);
    return preset?.label || '';
  };

  const submitTask = async (e) => {
    e.preventDefault();
    if (!form.projectId) return toast.error('Please pick a project');
    if (!form.taskType) return toast.error('Please pick a task type');
    if (form.taskType === 'custom' && !form.customText.trim()) return toast.error('Please type the task');
    if (!form.deadline) return toast.error('Please set a deadline');
    if (form.assignees.length === 0) return toast.error('Assign at least one team member');
    const description = resolveDescription();
    setSubmitting(true);
    try {
      await axios.post(`${API}/tasks`, {
        projectId: form.projectId,
        description,
        deadline: form.deadline,
        picName: form.assignees[0],
        assignedTo: form.assignees.map(name => ({ name, subDescription: '', deadline: form.deadline })),
        taskType: form.taskType,
      });
      toast.success('Task created');
      setForm({ projectId: '', taskType: '', customText: '', assignees: [], deadline: '' });
      const tk = await axios.get(`${API}/tasks`);
      setTasks(tk.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to create task');
    } finally {
      setSubmitting(false);
    }
  };

  const recentTasks = useMemo(() => {
    const now = Date.now();
    const ONE_DAY = 24 * 60 * 60 * 1000;
    return tasks
      .filter(t => {
        if (t.status === 'pending') return true;
        if (t.status === 'completed' && t.completedAt) {
          const ts = Date.parse(t.completedAt);
          return !isNaN(ts) && (now - ts) < ONE_DAY;
        }
        return false;
      })
      .sort((a, b) => (b.createdDate || '').localeCompare(a.createdDate || ''))
      .slice(0, 50);
  }, [tasks]);

  const tasksForProject = (projectId) => tasks
    .filter(t => t.projectId === projectId && t.status === 'pending')
    .sort((a, b) => (a.deadline || '').localeCompare(b.deadline || ''));

  const toggleTaskPerson = async (taskId, personName) => {
    try {
      await axios.put(`${API}/tasks/${taskId}/toggle-person/${encodeURIComponent(personName)}`);
      const tk = await axios.get(`${API}/tasks`);
      setTasks(tk.data);
    } catch { toast.error('Failed to update task'); }
  };

  // ============= Task file upload flow =============
  const fetchProjectFiles = async (projectId) => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}/files`);
      setProjectFiles(r.data);
    } catch { setProjectFiles([]); }
  };

  useEffect(() => {
    if (openProject) fetchProjectFiles(openProject.id);
    else setProjectFiles([]);
  }, [openProject]);

  const filesForPerson = (taskId, personName) =>
    projectFiles.filter(f => f.taskId === taskId && f.personName === personName);

  const startUpload = (taskId, personName, defaultFolder) => {
    setUploadFor({ taskId, personName, folderKey: defaultFolder || 'others' });
  };
  const cancelUpload = () => setUploadFor(null);

  const submitUpload = async (fileList) => {
    if (!uploadFor || !fileList || !fileList[0]) return;
    const file = fileList[0];
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('person_name', uploadFor.personName);
      fd.append('folder_key', uploadFor.folderKey);
      await axios.post(`${API}/tasks/${uploadFor.taskId}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded to "${FOLDER_LABEL(uploadFor.folderKey)}" — ${uploadFor.personName} checked`);
      setUploadFor(null);
      const [tk] = await Promise.all([
        axios.get(`${API}/tasks`),
        fetchProjectFiles(openProject.id),
      ]);
      setTasks(tk.data);
    } catch (e) {
      console.error(e);
      toast.error('Upload failed');
    } finally { setUploading(false); }
  };

  const uncheckPerson = async (taskId, personName) => {
    if (!window.confirm(`Uncheck ${personName}? This will remove their uploaded file(s) for this task.`)) return;
    try {
      await axios.delete(`${API}/tasks/${taskId}/check/${encodeURIComponent(personName)}`);
      const [tk] = await Promise.all([
        axios.get(`${API}/tasks`),
        fetchProjectFiles(openProject.id),
      ]);
      setTasks(tk.data);
      toast.success(`${personName} unchecked`);
    } catch { toast.error('Failed to uncheck'); }
  };

  const finishTask = async (taskId) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, { status: 'completed' });
      const tk = await axios.get(`${API}/tasks`);
      setTasks(tk.data);
      toast.success('Task finished');
    } catch { toast.error('Failed to finish task'); }
  };

  // ============= Project backup =============
  const backupProject = async (project) => {
    setBackingUp(prev => ({ ...prev, [project.id]: true }));
    try {
      const resp = await axios.get(`${API}/projects/${project.id}/backup`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      const dateStr = new Date().toISOString().slice(0,10);
      a.download = `${project.name.replace(/[^A-Za-z0-9 ._-]/g,'_')}_Backup_${dateStr}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
      toast.success('Backup downloaded');
      // Refresh project to pick up lastBackupAt
      const p = await axios.get(`${API}/projects?archived=false`);
      setProjects(p.data);
    } catch (e) {
      console.error(e);
      toast.error('Backup failed');
    } finally {
      setBackingUp(prev => ({ ...prev, [project.id]: false }));
    }
  };

  const startEditTask = (t) => {
    setEditingTaskId(t.id);
    setTaskEdit({ description: t.description || '', deadline: t.deadline || '' });
  };
  const cancelEditTask = () => { setEditingTaskId(null); setTaskEdit({ description: '', deadline: '' }); };
  const saveEditTask = async (taskId) => {
    if (!taskEdit.description.trim()) return toast.error('Task name cannot be empty');
    if (!taskEdit.deadline) return toast.error('Deadline is required');
    try {
      const existing = tasks.find(t => t.id === taskId);
      // Propagate the new deadline to every assignee so per-person overdue also refreshes
      const nextAssignedTo = (existing?.assignedTo || []).map(p => ({
        name: p.name,
        subDescription: p.subDescription || '',
        deadline: taskEdit.deadline,
      }));
      await axios.put(`${API}/tasks/${taskId}`, {
        description: taskEdit.description.trim(),
        deadline: taskEdit.deadline,
        assignedTo: nextAssignedTo,
      });
      const tk = await axios.get(`${API}/tasks`);
      setTasks(tk.data);
      cancelEditTask();
      toast.success('Task updated');
    } catch { toast.error('Failed to save task'); }
  };

  // ============= Project Dashboard =============
  const setStage = async (project, stageKey) => {
    try {
      await axios.put(`${API}/projects/${project.id}`, { engineeringStage: stageKey });
      setProjects(prev => prev.map(p => p.id === project.id ? { ...p, engineeringStage: stageKey } : p));
    } catch { toast.error('Failed to update stage'); }
  };

  const toggleMilestone = async (project, mKey) => {
    const next = { ...(project.engineeringMilestones || {}), [mKey]: !(project.engineeringMilestones || {})[mKey] };
    try {
      await axios.put(`${API}/projects/${project.id}`, { engineeringMilestones: next });
      setProjects(prev => prev.map(p => p.id === project.id ? { ...p, engineeringMilestones: next } : p));
    } catch { toast.error('Failed to update milestone'); }
  };

  const archiveFromEngineering = async (project) => {
    if ((project.pendingTasks || 0) > 0) {
      toast.error('Cannot archive — this project still has pending tasks.');
      return;
    }
    try {
      await axios.put(`${API}/projects/${project.id}`, { archived: true });
      toast.success(`Archived "${project.name}"`);
      setProjects(prev => prev.filter(p => p.id !== project.id));
    } catch { toast.error('Failed to archive project'); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="engineering-page">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/hub')} data-testid="back-to-hub">
              <ArrowLeft className="w-4 h-4 mr-1" /> Hub
            </Button>
            <div className="flex items-center gap-2">
              <HardHat className="w-6 h-6 text-slate-700" />
              <h1 className="text-2xl font-bold text-slate-800">Engineering</h1>
            </div>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList data-testid="engineering-tabs">
            <TabsTrigger value="input" data-testid="tab-input-task">Input Task</TabsTrigger>
            <TabsTrigger value="dashboard" data-testid="tab-project-dashboard">Project Dashboard</TabsTrigger>
            <TabsTrigger value="summary" data-testid="tab-team-summary">Team Summary</TabsTrigger>
          </TabsList>

          {/* ============= INPUT TASK ============= */}
          <TabsContent value="input">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
              <Card>
                <CardHeader><CardTitle>New Task</CardTitle></CardHeader>
                <CardContent>
                  <form onSubmit={submitTask} className="space-y-4">
                    <div>
                      <Label>Project</Label>
                      <Select value={form.projectId} onValueChange={(v) => setForm({ ...form, projectId: v })}>
                        <SelectTrigger data-testid="task-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
                        <SelectContent>
                          {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Task Type</Label>
                      <Select value={form.taskType} onValueChange={(v) => setForm({ ...form, taskType: v })}>
                        <SelectTrigger data-testid="task-type-select"><SelectValue placeholder="Select task type" /></SelectTrigger>
                        <SelectContent>
                          {TASK_TYPES.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                          <SelectItem value="custom">Custom (type below)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {form.taskType === 'custom' && (
                      <div>
                        <Label>Custom Task Description</Label>
                        <Textarea
                          value={form.customText}
                          onChange={(e) => setForm({ ...form, customText: e.target.value })}
                          placeholder="e.g. Add Detail Of Mezzanine"
                          data-testid="task-custom-text"
                        />
                      </div>
                    )}

                    <div>
                      <Label>Assign To (team members)</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {team.map(m => {
                          const active = form.assignees.includes(m.name);
                          return (
                            <button
                              type="button"
                              key={m.id}
                              onClick={() => toggleAssignee(m.name)}
                              className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${active ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-500'}`}
                              data-testid={`assign-${m.name}`}
                            >
                              {m.name}
                            </button>
                          );
                        })}
                        {team.length === 0 && <span className="text-xs text-slate-500">No team members yet</span>}
                      </div>
                    </div>

                    <div>
                      <Label>Deadline</Label>
                      <Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} data-testid="task-deadline" />
                    </div>

                    <Button type="submit" disabled={submitting} data-testid="submit-task-btn">
                      <Plus className="w-4 h-4 mr-1" /> {submitting ? 'Creating...' : 'Create Task'}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle>Active Tasks</CardTitle></CardHeader>
                <CardContent>
                  {loading ? (
                    <p className="text-sm text-slate-500">Loading...</p>
                  ) : recentTasks.length === 0 ? (
                    <p className="text-sm text-slate-500">No active tasks. Done tasks disappear from here after 24h.</p>
                  ) : (
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {recentTasks.map(t => {
                        const preset = TASK_TYPES.find(tp => tp.key === t.taskType);
                        const label = preset?.label || (t.taskType === 'custom' ? 'Custom' : null);
                        return (
                          <div key={t.id} className="p-3 border rounded bg-white flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                {label && <Badge variant="outline" className="text-[10px]">{label}</Badge>}
                                <span className="text-sm font-medium text-slate-800 truncate">{t.description}</span>
                              </div>
                              <p className="text-xs text-slate-500 mt-1">{t.projectName} · {(t.assignedTo || []).map(a => a.name).join(', ')}</p>
                            </div>
                            <div className="text-right shrink-0">
                              <Badge className={t.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                                {t.status}
                              </Badge>
                              <p className="text-[10px] text-slate-500 mt-1 flex items-center gap-1 justify-end">
                                <Calendar className="w-3 h-3" /> {t.deadline}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ============= PROJECT DASHBOARD ============= */}
          <TabsContent value="dashboard">
            {/* Book sub-filter */}
            <div className="flex items-center gap-2 mt-4 mb-3">
              <button
                type="button"
                onClick={() => setBookFilter('freelance')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${bookFilter === 'freelance' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-500'}`}
                data-testid="book-filter-freelance"
              >
                Freelance ({projects.filter(p => (p.financeBook || 'freelance') === 'freelance').length})
              </button>
              <button
                type="button"
                onClick={() => setBookFilter('company')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${bookFilter === 'company' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-500'}`}
                data-testid="book-filter-company"
              >
                Company ({projects.filter(p => p.financeBook === 'company').length})
              </button>
            </div>
            {(() => {
              const filtered = projects.filter(p => (p.financeBook || 'freelance') === bookFilter);
              if (loading) return <p className="text-sm text-slate-500 mt-6">Loading...</p>;
              if (filtered.length === 0) return <p className="text-sm text-slate-500 mt-6">No active {bookFilter} projects.</p>;
              return (
              <div className="space-y-4">
                {filtered.map(project => {
                  const currentStageIdx = STAGES.findIndex(s => s.key === project.engineeringStage);
                  const milestones = project.engineeringMilestones || {};
                  const canArchive = (project.pendingTasks || 0) === 0;
                  const projectPending = tasks.filter(t => t.projectId === project.id && t.status === 'pending').length;
                  return (
                    <Card
                      key={project.id}
                      data-testid={`eng-project-${project.id}`}
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => setOpenProject(project)}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold text-slate-800">{project.name}</h3>
                            <Badge variant="outline" className="text-[10px]">
                              {projectPending} pending task{projectPending === 1 ? '' : 's'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <Badge className={(project.financeBook || 'freelance') === 'company' ? 'bg-indigo-100 text-indigo-800' : 'bg-purple-100 text-purple-800'}>
                              {(project.financeBook || 'freelance') === 'company' ? 'Company' : 'Freelance'}
                            </Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={!!backingUp[project.id]}
                              onClick={(e) => { e.stopPropagation(); backupProject(project); }}
                              title="Download project backup (ZIP organized by folder tree)"
                              data-testid={`eng-backup-${project.id}`}
                            >
                              <Package className="w-4 h-4 text-slate-700" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={!canArchive}
                              onClick={(e) => { e.stopPropagation(); archiveFromEngineering(project); }}
                              title={canArchive ? 'Archive this project (no pending tasks)' : `Cannot archive — ${project.pendingTasks} pending task(s)`}
                              data-testid={`eng-archive-${project.id}`}
                            >
                              <Archive className={`w-4 h-4 ${canArchive ? 'text-slate-700' : 'text-slate-300'}`} />
                            </Button>
                          </div>
                        </div>
                        {project.lastBackupAt && (
                          <p className="text-[11px] text-slate-500 -mt-3 mb-4">
                            Last backup: {new Date(project.lastBackupAt).toLocaleString()} · {project.lastBackupFileCount || 0} file{(project.lastBackupFileCount || 0) === 1 ? '' : 's'}
                          </p>
                        )}

                        {/* Status Stepper */}
                        <div className="mb-5" onClick={(e) => e.stopPropagation()}>
                          <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Status</p>
                          <div className="flex items-center gap-1">
                            {STAGES.map((s, i) => {
                              const isActive = i === currentStageIdx;
                              const isPast = currentStageIdx >= 0 && i < currentStageIdx;
                              return (
                                <React.Fragment key={s.key}>
                                  <button
                                    type="button"
                                    onClick={() => setStage(project, isActive ? '' : s.key)}
                                    className={`flex-1 py-2 px-3 rounded text-xs font-medium border transition-colors ${
                                      isActive ? 'bg-blue-600 text-white border-blue-600' :
                                      isPast ? 'bg-blue-100 text-blue-800 border-blue-200' :
                                      'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                                    }`}
                                    data-testid={`stage-${project.id}-${s.key}`}
                                    title={isActive ? 'Click again to clear stage' : `Set stage: ${s.label}`}
                                  >
                                    {isPast && <Check className="w-3 h-3 inline mr-1" />}
                                    {s.label}
                                  </button>
                                  {i < STAGES.length - 1 && <div className="w-3 h-px bg-slate-300" />}
                                </React.Fragment>
                              );
                            })}
                          </div>
                          {currentStageIdx === STAGES.length - 1 && (
                            <p className="text-[11px] text-slate-500 mt-2 italic">
                              Waiting phase — close manually when the project is fully finished.
                            </p>
                          )}
                        </div>

                        {/* Milestone Checklist */}
                        <div onClick={(e) => e.stopPropagation()}>
                          <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Milestones</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {TASK_TYPES.map(m => {
                              const done = !!milestones[m.key];
                              return (
                                <label
                                  key={m.key}
                                  className={`flex items-center gap-2 p-2 border rounded cursor-pointer transition-colors ${done ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200 hover:border-slate-400'}`}
                                  data-testid={`milestone-${project.id}-${m.key}`}
                                >
                                  <Checkbox checked={done} onCheckedChange={() => toggleMilestone(project, m.key)} />
                                  <span className={`text-sm ${done ? 'text-green-800 line-through' : 'text-slate-700'}`}>{m.label}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              );
            })()}
          </TabsContent>

          {/* ============= TEAM SUMMARY ============= */}
          <TabsContent value="summary">
            {(() => {
              // Build per-member summary
              const projectMap = Object.fromEntries(projects.map(p => [p.id, p]));
              const now = Date.now();
              const membersMap = {}; // name -> { pending: [], overdue: 0 }
              for (const t of tasks) {
                if (t.status !== 'pending') continue;
                const proj = projectMap[t.projectId];
                const book = proj?.financeBook || 'freelance';
                if (summaryBook !== 'all' && book !== summaryBook) continue;
                const preset = TASK_TYPES.find(tp => tp.key === t.taskType);
                for (const person of (t.assignedTo || [])) {
                  if (person.completed) continue;
                  const overdue = person.deadline
                    ? (Date.parse(person.deadline) < now && !isNaN(Date.parse(person.deadline)))
                    : (t.deadline && Date.parse(t.deadline) < now);
                  const row = {
                    taskId: t.id,
                    description: t.description,
                    taskType: t.taskType,
                    taskTypeLabel: preset?.label,
                    projectName: proj?.name || t.projectName || '-',
                    financeBook: book,
                    deadline: person.deadline || t.deadline,
                    subDescription: person.subDescription || '',
                    personName: person.name,
                    overdue: !!overdue,
                  };
                  if (!membersMap[person.name]) membersMap[person.name] = { pending: [], overdue: 0 };
                  membersMap[person.name].pending.push(row);
                  if (overdue) membersMap[person.name].overdue += 1;
                }
              }
              // Ensure team members with 0 tasks still show if not searching
              for (const m of team) {
                if (!membersMap[m.name]) membersMap[m.name] = { pending: [], overdue: 0 };
              }
              let members = Object.entries(membersMap).map(([name, v]) => ({
                name,
                pending: v.pending.sort((a, b) => (a.deadline || '').localeCompare(b.deadline || '')),
                overdue: v.overdue,
              }));
              if (summarySearch.trim()) {
                const q = summarySearch.toLowerCase();
                members = members.filter(m => m.name.toLowerCase().includes(q));
              }
              if (summaryOverdueOnly) members = members.filter(m => m.overdue > 0);
              members.sort((a, b) => b.overdue - a.overdue || b.pending.length - a.pending.length || a.name.localeCompare(b.name));

              return (
                <>
                  {/* Filters */}
                  <div className="flex flex-wrap items-center gap-2 mt-4 mb-3">
                    <div className="relative">
                      <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
                      <Input
                        placeholder="Search member..."
                        value={summarySearch}
                        onChange={(e) => setSummarySearch(e.target.value)}
                        className="pl-7 h-8 w-56"
                        data-testid="summary-search"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setSummaryOverdueOnly(v => !v)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border ${summaryOverdueOnly ? 'bg-red-600 text-white border-red-600' : 'bg-white text-slate-700 border-slate-300'}`}
                      data-testid="summary-overdue-toggle"
                    >
                      {summaryOverdueOnly ? 'Overdue only' : 'Show overdue only'}
                    </button>
                    <div className="flex items-center gap-1">
                      {['all', 'freelance', 'company'].map(b => (
                        <button
                          key={b}
                          type="button"
                          onClick={() => setSummaryBook(b)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border ${summaryBook === b ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-300'}`}
                        >
                          {b === 'all' ? 'All Books' : (b === 'freelance' ? 'Freelance' : 'Company')}
                        </button>
                      ))}
                    </div>
                  </div>

                  {members.length === 0 ? (
                    <p className="text-sm text-slate-500 mt-6">No team members match this filter.</p>
                  ) : (
                    <div className="space-y-2">
                      {members.map(m => {
                        const expanded = !!expandedMembers[m.name];
                        // Group pending by project
                        const byProject = {};
                        for (const t of m.pending) {
                          if (!byProject[t.projectName]) byProject[t.projectName] = [];
                          byProject[t.projectName].push(t);
                        }
                        return (
                          <Card key={m.name} data-testid={`member-summary-${m.name}`}>
                            <CardContent className="p-0">
                              <div
                                className="p-3 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                                onClick={() => setExpandedMembers(prev => ({ ...prev, [m.name]: !expanded }))}
                              >
                                <div className="flex items-center gap-2">
                                  {expanded ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                                  <span className="font-medium text-slate-800">{m.name}</span>
                                  <Badge variant="outline" className="text-[10px]">{m.pending.length} pending</Badge>
                                  {m.overdue > 0 && (
                                    <Badge className="bg-red-100 text-red-800 text-[10px]">{m.overdue} overdue</Badge>
                                  )}
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={m.pending.length === 0}
                                  onClick={(e) => { e.stopPropagation(); generateTeamMemberTasksPDF({ memberName: m.name, tasks: m.pending }); }}
                                  data-testid={`member-pdf-${m.name}`}
                                >
                                  <FileText className="w-3.5 h-3.5 mr-1" /> PDF
                                </Button>
                              </div>
                              {expanded && (
                                <div className="border-t px-3 py-2 space-y-3">
                                  {m.pending.length === 0 ? (
                                    <p className="text-xs text-slate-500 italic py-2">No pending tasks.</p>
                                  ) : (
                                    Object.entries(byProject).map(([projName, list]) => (
                                      <div key={projName}>
                                        <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">{projName}</p>
                                        <div className="space-y-1">
                                          {list.map((t, i) => (
                                            <div key={t.taskId + i} className="flex items-center justify-between text-sm border rounded px-2 py-1 bg-white">
                                              <div className="flex items-center gap-2 min-w-0">
                                                {t.taskTypeLabel && <Badge variant="outline" className="text-[10px]">{t.taskTypeLabel}</Badge>}
                                                <span className="truncate text-slate-700">{t.description}</span>
                                                {t.subDescription && <span className="text-xs text-slate-500 truncate">— {t.subDescription}</span>}
                                              </div>
                                              <span className={`text-xs flex items-center gap-1 whitespace-nowrap ${t.overdue ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>
                                                <Calendar className="w-3 h-3" /> {t.deadline || '-'}{t.overdue ? ' · OVERDUE' : ''}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </>
              );
            })()}
          </TabsContent>
        </Tabs>

        {/* Project pending-tasks dialog */}
        <Dialog open={!!openProject} onOpenChange={(o) => !o && setOpenProject(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="project-tasks-dialog">
            {openProject && (
              <>
                <DialogHeader>
                  <DialogTitle>{openProject.name} — Pending Tasks</DialogTitle>
                </DialogHeader>
                <div className="space-y-2 py-2">
                  {tasksForProject(openProject.id).length === 0 ? (
                    <p className="text-sm text-slate-500 italic">No pending tasks for this project.</p>
                  ) : (
                    tasksForProject(openProject.id).map(t => {
                      const normKey = normalizeTaskTypeKey(t.taskType);
                      const preset = TASK_TYPES.find(tp => tp.key === normKey);
                      const label = preset?.label || (t.taskType === 'custom' ? 'Custom' : null);
                      const isEditing = editingTaskId === t.id;
                      const assignees = t.assignedTo || [];
                      const allChecked = assignees.length > 0 && assignees.every(p => p.completed);
                      const defaultFolder = TASK_TYPES.find(x => x.key === normKey) ? normKey : 'others';
                      return (
                        <div key={t.id} className="p-3 border rounded bg-white">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                {label && <Badge variant="outline" className="text-[10px]">{label}</Badge>}
                                {isEditing ? (
                                  <Input
                                    value={taskEdit.description}
                                    onChange={(e) => setTaskEdit({ ...taskEdit, description: e.target.value })}
                                    className="h-7 text-sm flex-1 min-w-[200px]"
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') saveEditTask(t.id);
                                      if (e.key === 'Escape') cancelEditTask();
                                    }}
                                    data-testid={`edit-task-name-${t.id}`}
                                  />
                                ) : (
                                  <>
                                    <span className="text-sm font-medium text-slate-800">{t.description}</span>
                                    <Button size="sm" variant="ghost" className="h-6 p-1" onClick={() => startEditTask(t)} data-testid={`edit-task-btn-${t.id}`}>
                                      <Pencil className="w-3 h-3 text-slate-500" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                            {isEditing ? (
                              <div className="flex items-center gap-1 whitespace-nowrap">
                                <Input
                                  type="date"
                                  value={taskEdit.deadline}
                                  onChange={(e) => setTaskEdit({ ...taskEdit, deadline: e.target.value })}
                                  className="h-7 text-xs w-36"
                                  data-testid={`edit-task-deadline-${t.id}`}
                                />
                                <Button size="sm" className="h-7 bg-green-600 hover:bg-green-700" onClick={() => saveEditTask(t.id)} data-testid={`save-task-${t.id}`}>
                                  <Check className="w-3 h-3" />
                                </Button>
                                <Button size="sm" variant="outline" className="h-7" onClick={cancelEditTask}>
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            ) : (
                              <p className="text-[11px] text-slate-500 flex items-center gap-1 whitespace-nowrap">
                                <Calendar className="w-3 h-3" /> {t.deadline}
                              </p>
                            )}
                          </div>

                          {/* Per-person job route */}
                          <div className="space-y-1.5 pl-2 border-l-2 border-slate-100 mb-2">
                            {assignees.map(p => {
                              const files = filesForPerson(t.id, p.name);
                              const isUpload = uploadFor && uploadFor.taskId === t.id && uploadFor.personName === p.name;
                              return (
                                <div key={p.name} className="text-sm">
                                  <div className="flex items-center justify-between gap-2 hover:bg-slate-50 rounded px-1 py-1">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <span className={`inline-flex items-center justify-center w-4 h-4 rounded border ${p.completed ? 'bg-green-500 border-green-500 text-white' : 'border-slate-300 bg-white'}`}>
                                        {p.completed && <Check className="w-3 h-3" />}
                                      </span>
                                      <span className={p.completed ? 'line-through text-slate-500' : 'text-slate-700'}>
                                        <b>{p.name}</b>{p.subDescription ? ` — ${p.subDescription}` : ''}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                      {p.completed ? (
                                        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-red-600 hover:bg-red-50" onClick={() => uncheckPerson(t.id, p.name)} data-testid={`uncheck-${t.id}-${p.name}`}>
                                          Uncheck
                                        </Button>
                                      ) : (
                                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => startUpload(t.id, p.name, defaultFolder)} data-testid={`upload-${t.id}-${p.name}`}>
                                          <Upload className="w-3 h-3 mr-1" /> Upload
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                  {files.length > 0 && (
                                    <div className="pl-6 flex flex-wrap gap-1 mt-0.5">
                                      {files.map(f => (
                                        <span key={f.id} className="inline-flex items-center gap-1 text-[11px] text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">
                                          <Paperclip className="w-2.5 h-2.5" /> {f.originalName} <span className="text-slate-400">· {f.folderLabel}</span>
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {isUpload && (
                                    <div className="pl-6 mt-1 flex items-center gap-1.5 flex-wrap bg-blue-50 p-2 rounded">
                                      <span className="text-[11px] text-slate-600">Folder:</span>
                                      <Select value={uploadFor.folderKey} onValueChange={(v) => setUploadFor({ ...uploadFor, folderKey: v })}>
                                        <SelectTrigger className="h-7 text-xs w-56" data-testid={`upload-folder-${t.id}-${p.name}`}><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                          {TASK_TYPES.map(x => <SelectItem key={x.key} value={x.key}>{x.label}</SelectItem>)}
                                        </SelectContent>
                                      </Select>
                                      <input
                                        type="file"
                                        onChange={(e) => submitUpload(e.target.files)}
                                        disabled={uploading}
                                        className="text-xs"
                                        data-testid={`upload-file-${t.id}-${p.name}`}
                                      />
                                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={cancelUpload}>Cancel</Button>
                                      {uploading && <span className="text-[11px] text-blue-600">Uploading…</span>}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* Finish Task */}
                          <div className="flex justify-end">
                            <Button
                              size="sm"
                              disabled={!allChecked}
                              onClick={() => finishTask(t.id)}
                              className={allChecked ? 'bg-green-600 hover:bg-green-700' : ''}
                              title={allChecked ? 'Mark this task as finished' : 'All assignees must be checked first'}
                              data-testid={`finish-task-${t.id}`}
                            >
                              <Check className="w-3 h-3 mr-1" /> Finish Task
                            </Button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default EngineeringPage;
